import React, { useCallback, useMemo, useState } from 'react';
import { api } from '@/api/client';
import MaintenanceCleanupModal, {
  type MaintenanceCleanupModalConfig,
} from '@/components/maintenance/MaintenanceCleanupModal';
import PageHeader from '@/components/PageHeader';
import { AdminPageSections } from '@/components/common';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { useMaintenanceCleanupEligibility } from '@/hooks/useMaintenanceCleanupEligibility';
import { invalidateCustomerActivityRefresh } from '@/utils/customerActivityRefresh';

type MaintenanceActionDefinition = {
  id: string;
  title: string;
  description: string;
  buttonLabel: string;
  disabledTitle: string;
  modalTitle: string;
  confirmWarning: string;
  loadPreview: () => Promise<{ eligibleCount: number; canClear?: boolean }>;
  executeCleanup: () => Promise<{ deleted: number; executionMs?: number }>;
};

const NOTIFICATIONS_CONFIRM_WARNING =
  'This will permanently remove notification and activity feed history shown in the notification bell, including delivery logs, in-app notifications, lifecycle reminders, search/subscription created events, and read markers. Searches, subscriptions, recipients, listings, team audit history, and account configuration are not affected.';

function useMaintenanceActions(): MaintenanceActionDefinition[] {
  return useMemo(
    () => [
      {
        id: 'notifications',
        title: 'Notification history',
        description:
          'Remove notification bell history: delivery logs, in-app notifications, lifecycle reminders, activity feed events, and read markers. Team audit events and new listing records are kept.',
        buttonLabel: 'Clear notifications',
        disabledTitle: 'No notifications available to remove.',
        modalTitle: 'Clear notifications?',
        confirmWarning: NOTIFICATIONS_CONFIRM_WARNING,
        loadPreview: async () => {
          const result = await api.maintenance.notificationsCleanupPreview({});
          return {
            eligibleCount: result.eligibleCount ?? result.matched ?? 0,
            canClear: result.canClear ?? (result.eligibleCount ?? 0) > 0,
          };
        },
        executeCleanup: () => api.maintenance.notificationsCleanup({}),
      },
    ],
    [],
  );
}

export default function MaintenancePage() {
  const { toast } = useToast();
  const { me } = useMe();
  const canManageJobs = me?.permissions.canManageJobs ?? false;
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const maintenanceCleanupActions = useMaintenanceActions();

  const notificationsAction = useMemo(
    () => maintenanceCleanupActions.find((action) => action.id === 'notifications'),
    [maintenanceCleanupActions],
  );

  const notificationsPreviewLoader = useCallback(async () => {
    if (!notificationsAction) {
      return { eligibleCount: 0, canClear: false };
    }
    return notificationsAction.loadPreview();
  }, [notificationsAction]);

  const notificationsEligibility = useMaintenanceCleanupEligibility(
    canManageJobs,
    notificationsPreviewLoader,
    refreshSignal,
  );

  const openAction = maintenanceCleanupActions.find((action) => action.id === openActionId) ?? null;

  const modalConfig: MaintenanceCleanupModalConfig | null = openAction
    ? {
        title: openAction.modalTitle,
        confirmWarning: openAction.confirmWarning,
        loadPreview: openAction.loadPreview,
        executeCleanup: openAction.executeCleanup,
      }
    : null;

  async function handleDone(message: string) {
    toast(message, message.includes('No ') ? 'info' : 'success');
    invalidateCustomerActivityRefresh();
    setRefreshSignal((value) => value + 1);
    await notificationsEligibility.refresh();
  }

  if (!canManageJobs) {
    return (
      <>
        <PageHeader title="Maintenance" subtitle="Operational cleanup and housekeeping" />
        <p className="muted">Maintenance actions require owner or admin permissions.</p>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Maintenance"
        subtitle="Safe cleanup actions for operational history. Core configuration and account data are never removed."
      />

      <AdminPageSections>
        <section className="card card-elevated maintenance-action-card">
          <h2>Notification cleanup</h2>
          <p className="muted">{notificationsAction?.description}</p>
          <p className="maintenance-action-card__count">
            {notificationsEligibility.loading
              ? 'Checking eligible notification records…'
              : `${notificationsEligibility.eligibleCount} notification record(s) eligible for removal`}
          </p>
          <div className="maintenance-action-card__actions">
            <button
              type="button"
              className="btn btn-danger btn-sm"
              disabled={!notificationsEligibility.canClear}
              title={
                !notificationsEligibility.canClear && !notificationsEligibility.loading
                  ? notificationsAction?.disabledTitle
                  : undefined
              }
              onClick={() => {
                if (!notificationsEligibility.canClear) return;
                setOpenActionId('notifications');
              }}
            >
              {notificationsAction?.buttonLabel}
            </button>
          </div>
        </section>
      </AdminPageSections>

      {modalConfig ? (
        <MaintenanceCleanupModal
          config={modalConfig}
          onClose={() => setOpenActionId(null)}
          onDone={handleDone}
        />
      ) : null}
    </>
  );
}
